import { debounce } from 'lodash-es'
import {
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'
import HiddenItemsMenu from './HiddenItemsMenu'
import MenuItem, {
  IMenuItem,
} from 'src/features/navigation/TopNavigation/components/MenuItemLinks/MenuItem'
import { useMenuItems } from 'src/hooks/useMenuItems'

interface Props {
  gap: number
  collapsedHeader?: ReactNode
  statusIcon?: (isOpen: boolean) => ReactElement
}

interface UIElementBase {
  width: number
}

type UIElement<T extends ReactNode> = T & UIElementBase

const ResponsiveMenu = (props: Props): ReactElement => {
  const { collapsedHeader, gap, statusIcon } = props
  const wrapperRef = useRef<HTMLDivElement>(undefined)
  const [showDropdown, setShowDropdown] = useState(true) // Show the dropdown first to get its width, then hide if necessary.
  const [openDropdown, setOpenDropdown] = useState(false)
  const collapedHeaderWidth = useRef<number>(0)
  const rowMenuRef = useRef<HTMLUListElement>(undefined)
  const collapsedHeaderRef = useRef<HTMLUListElement>(undefined)
  const [hiddenItems, setHiddenItems] = useState<ReactNode[]>([])
  const allElements = useRef<UIElement<ReactNode>[]>([])
  const [menuWidth, setMenuWidth] = useState<number>(0)

  const menuItems: IMenuItem[] = useMenuItems()
  const items = menuItems.map((i) => (
    <MenuItem key={`menu_item_${i.text}`} {...i} />
  ))
  const [visibleItems, setVisibleItems] = useState<ReactNode[]>([...items])

  const collectAllElementsWidth = useCallback(() => {
    const uiElements = Array.from(rowMenuRef?.current?.children || [])

    const elements = uiElements?.map((e, idx) => {
      return {
        ...items[idx]?.props,
        width: e.clientWidth,
      }
    })

    allElements.current = elements || []
    const dropdownWidth = collapsedHeaderRef?.current?.offsetWidth ?? 0
    collapedHeaderWidth.current = dropdownWidth
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleResize = useCallback(() => {
    const isElementsWithCollected = allElements?.current?.length
    if (!isElementsWithCollected) collectAllElementsWidth()
    const dropdownWidth = collapedHeaderWidth.current
    const reservedWidth = isElementsWithCollected ? 0 : dropdownWidth
    const availableWidth = wrapperRef?.current?.clientWidth - reservedWidth
    setMenuWidth(availableWidth)

    let usedWidth = 0
    const hidden = []
    const visible = []

    for (let i = 0; i < allElements.current.length; i++) {
      const element = allElements.current[i]
      const requiredWith = usedWidth + element.width + gap
      if (requiredWith < availableWidth) {
        visible.push(items[i])
        usedWidth = requiredWith
      } else {
        const firstHiddenElement = hidden?.length <= 0
        if (firstHiddenElement) {
          // collapsing the last two elements
          hidden.push(items[i])
          hidden.push(items[i - 1])
          visible.pop()
        } else {
          hidden.push(items[i])
        }
      }
    }

    setVisibleItems(visible)
    setHiddenItems(hidden)
    setShowDropdown(collapsedHeader && hidden?.length > 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    handleResize()
  }, [handleResize, menuWidth])

  useEffect(() => {
    const resize = debounce(handleResize, 100)
    window.addEventListener('resize', resize)
    return () => window.removeEventListener('resize', resize)
  }, [handleResize])

  const toggleDropdown = () => {
    setOpenDropdown(!openDropdown)
  }

  const icon = statusIcon ? statusIcon(openDropdown) : null

  return (
    <Wrapper ref={wrapperRef}>
      <UL ref={rowMenuRef} gap={gap}>
        {visibleItems.map((item, idx) => (
          <LI key={`item_${idx}`}>{item}</LI>
        ))}
      </UL>
      {showDropdown && (
        <UL ref={collapsedHeaderRef} style={{ marginLeft: gap }}>
          <MenuHeader onClick={toggleDropdown}>
            {collapsedHeader} {icon}
          </MenuHeader>
          <HiddenItemsMenu
            open={openDropdown}
            onClose={toggleDropdown}
            parentRef={collapsedHeaderRef}
            items={hiddenItems}
          />
        </UL>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
`

interface ULProps {
  gap?: number
}

const UL = styled.ul<ULProps>`
  display: flex;
  flex-direction: row;
  list-style-type: none;
  padding: 0px;
  gap: ${(p) => p.gap}px;
`

const LI = styled.li``

const MenuHeader = styled.div`
  display: flex;
  align-items: center;
`

export default ResponsiveMenu
